/**
 * Status Column Pattern
 * 
 * Creates status columns for enum/string values with StatusBadge variants.
 * For boolean values, use booleanColumn instead.
 */

import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { StatusBadge } from '@etherisc/ui-kit';
import { getEnumLabel, getStatusBadgeVariant } from '../../constants/enumLabels';
import type { StatusColumnOptions } from './types';

/**
 * Creates a status column for enum/string values only
 * 
 * @param accessor - The data accessor key (should contain enum/string values)
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // With enum category
 * statusColumn('status', {
 *   enumCategory: 'userStatus'
 * })
 * 
 * // With custom variant mapping
 * statusColumn('role', {
 *   customVariants: {
 *     admin: 'success',
 *     user: 'info',
 *     guest: 'neutral'
 *   }
 * })
 * 
 * // Simple enum with default variant
 * statusColumn('type') // All values get 'info' variant
 */
export function statusColumn<T>(
  accessor: keyof T,
  options: StatusColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    enumCategory,
    customLabels,
    customVariants,
    defaultVariant = 'info',
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    cell: ({ row }) => {
      // Use custom getValue function if provided, otherwise use accessor
      const rawValue = options.getValue 
        ? options.getValue(row.original)
        : row.original[accessor];
      
      const value = String(rawValue || '');
      
      let label: string;
      let variant: 'success' | 'error' | 'warning' | 'info' | 'pending' | 'neutral';
      
      // Handle enum values with category
      if (enumCategory && typeof value === 'string') {
        label = getEnumLabel(enumCategory as keyof typeof import('../../constants/enumLabels').ENUM_LABELS, value);
        variant = getStatusBadgeVariant(enumCategory as keyof typeof import('../../constants/enumLabels').STATUS_BADGE_VARIANTS, value) as 'success' | 'error' | 'warning' | 'info' | 'pending' | 'neutral';
      }
      // Handle custom variant mapping
      else if (customVariants && customVariants[value]) {
        label = customLabels?.[value] || value;
        variant = customVariants[value];
      }
      // Default: use value as label and default variant
      else {
        label = value;
        variant = defaultVariant;
      }
      
      // Apply custom label transformation if provided
      if (options.transformLabel) {
        label = options.transformLabel(label);
      }
      
      // Use StatusBadge from ui-kit
      return React.createElement(StatusBadge, { variant, children: label });
    },
  };
}

/**
 * Creates a role-based status column with smart variants
 */
export function roleStatusColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return statusColumn(accessor, {
    header,
    customVariants: {
      admin: 'success',
      product_admin: 'warning',
      system_operator: 'info',
      reinsurer: 'neutral',
      user: 'neutral',
    },
  });
}

/**
 * Creates an enum-based status column
 */
export function enumStatusColumn<T>(
  accessor: keyof T, 
  enumCategory: string,
  header?: string
): ColumnDef<T> {
  return statusColumn(accessor, {
    header,
    enumCategory,
  });
} 